import {
  createContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { applyTheme } from '../lib/theme';
import { ROLES } from '../lib/roles';
import type {
  Distributor,
  UserRole,
  AppTheme,
  RowDensity,
  Organization,
  BuyingSessionPrefs,
} from '../types';

// ── Context shape ─────────────────────────────────────────────────────────────

interface AppContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  theme: AppTheme;
  density: RowDensity;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  confirmResets: boolean;
  setConfirmResets: (v: boolean) => void;
  toggleTheme: () => void;
  toggleDensity: () => void;
  distributors: Distributor[];
  /** Re-fetch the active-distributors list from Supabase and update the
   *  in-context `distributors` state. Returns the freshly fetched list so
   *  callers can derive state from authoritative data (avoids the
   *  returned-list-vs-state-async race). Does NOT re-seed `activeDistributor`
   *  on subsequent calls — first-mount-only seeding per D.14 M1-a SEED-ONCE
   *  semantic lock (Sol-locked 2026-05-24). */
  refreshDistributors: () => Promise<Distributor[]>;
  activeDistributor: Distributor | null;
  setActiveDistributor: (d: Distributor | null) => void;
  creditBalance: number | null;
  cashBalance: number | null;
  role: UserRole | null;
  isAdmin: boolean;
  isDistRep: boolean;
  isViewer: boolean;
  profileDistributorId: string | null;
  fullName: string | null;
  setFullName: (n: string | null) => void;
  organization: Organization | null;
  setOrganization: (o: Organization | null) => void;
  buyingSessionPrefs: BuyingSessionPrefs | null;
  setBuyingSessionPrefs: (p: BuyingSessionPrefs | null) => void;
  signOut: () => Promise<void>;
}

// Context instance colocated with its provider by design.
// Fast-refresh warns when a file mixes components and non-component exports;
// the dev-HMR cost here is acceptable vs the clarity of having AppContext and
// AppProvider in the same file. useApp (the consumer hook) lives in
// src/hooks/useApp.ts so component files don't fall back to full reloads.
// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext<AppContextValue | null>(null);

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [theme, setTheme] = useState<AppTheme>(
    () => (localStorage.getItem('arbi-theme') as AppTheme) || 'dark'
  );
  const [density, setDensity] = useState<RowDensity>(
    () => (localStorage.getItem('arbi-density') as RowDensity) || 'comfortable'
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [confirmResets, setConfirmResets] = useState<boolean>(() => {
    const saved = localStorage.getItem('arbi-confirm-resets');
    return saved === null ? true : saved === 'true';
  });

  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [activeDistributor, setActiveDistributor] = useState<Distributor | null>(null);

  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [cashBalance, setCashBalance] = useState<number | null>(null);

  const [role, setRole] = useState<UserRole | null>(null);
  const [profileDistributorId, setProfileDistributorId] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [buyingSessionPrefs, setBuyingSessionPrefs] = useState<BuyingSessionPrefs | null>(null);
  const isAdmin = role === ROLES.ADMIN;
  const isDistRep = role === ROLES.DIST_REP;
  const isViewer = role === ROLES.VIEWER;

  useLayoutEffect(() => {
    applyTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load user profile (role, distributor scope, buying session prefs) once authenticated.
  // Single fetch; buying_session_prefs piggybacks so Settings → Profile reads from
  // the same in-memory copy ParamsSidebar consumes — no extra round trip.
  useEffect(() => {
    if (!user) {
      setRole(null);
      setProfileDistributorId(null);
      setFullName(null);
      setBuyingSessionPrefs(null);
      return;
    }

    (async () => {
      // DD-28: a transient profile-fetch failure must NOT grant 'user' chrome to an
      // unidentified session. Retry a couple times, then fall back to LEAST-PRIVILEGE
      // (viewer = read-only, no write/admin affordances) — server RLS + SECURITY-DEFINER
      // gates bound data exposure regardless of UI role; this closes the affordance leak.
      const FETCH_RETRIES = 2;
      for (let attempt = 0; attempt <= FETCH_RETRIES; attempt += 1) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('role, distributor_id, full_name, is_active, buying_session_prefs')
            .eq('id', user.id)
            .single();
          if (error || !data) {
            if (attempt < FETCH_RETRIES) {
              await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
              continue;
            }
            console.warn(
              '[Arbi] Profile fetch failed after retries — defaulting to least-privilege (viewer):',
              error?.message
            );
            setRole(ROLES.VIEWER as UserRole);
            setProfileDistributorId(null);
            setFullName(null);
            setBuyingSessionPrefs(null);
            return;
          }
          if (data.is_active === false) {
            await supabase.auth.signOut();
            window.location.replace('/login?reason=deactivated');
            return;
          }
          // A fetched row with a null role is a data anomaly, not 'user' — least-privilege.
          setRole((data.role as UserRole) || (ROLES.VIEWER as UserRole));
          setProfileDistributorId(data.distributor_id);
          setFullName(data.full_name);
          setBuyingSessionPrefs(data.buying_session_prefs as BuyingSessionPrefs | null);
          return;
        } catch (err) {
          if (attempt < FETCH_RETRIES) {
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
            continue;
          }
          console.warn(
            '[Arbi] Profile fetch unavailable after retries — defaulting to least-privilege (viewer):',
            (err as Error).message
          );
          setRole(ROLES.VIEWER as UserRole);
          setProfileDistributorId(null);
          setFullName(null);
          setBuyingSessionPrefs(null);
          return;
        }
      }
    })();
  }, [user]);

  // Load org metadata once authenticated. Singleton row, low write frequency,
  // no Realtime subscription — page reload picks up edits.
  useEffect(() => {
    if (!user) {
      setOrganization(null);
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from('organization')
          .select('*')
          .eq('id', 1)
          .single();
        if (error || !data) {
          console.warn('[Arbi] Organization fetch failed:', error?.message);
          return;
        }
        setOrganization(data as unknown as Organization);
      } catch (err) {
        console.warn('[Arbi] Organization fetch unavailable:', (err as Error).message);
      }
    })();
  }, [user]);

  // Day 92b G.1 — idle-watcher enforcement. Reads
  // organization.session_timeout_minutes (DEFAULT 240, CHECK 5..1440 at DB
  // layer); on activity-free period exceeding the policy, signs the user out
  // and redirects to /login?reason=idle. Per Sol Checkpoint #1 GREEN
  // 2026-05-28 — enforcement is real, not display-only.
  //
  // Activity listeners are passive + debounced (~1s) so the timer reset
  // doesn't fire 100Hz under continuous mouse-move; the reset itself is
  // cheap (one clearTimeout + one setTimeout). The effect re-subscribes
  // when session_timeout_minutes changes so timeout edits in Settings →
  // Security take effect immediately without a reload.
  useEffect(() => {
    if (!user || !organization?.session_timeout_minutes) return undefined;

    const timeoutMs = organization.session_timeout_minutes * 60_000;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let debounceArmed = false;

    function onIdle(): void {
      void (async () => {
        await supabase.auth.signOut();
        window.location.replace('/login?reason=idle');
      })();
    }

    function resetIdleTimer(): void {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(onIdle, timeoutMs);
    }

    function onActivity(): void {
      if (debounceArmed) return;
      debounceArmed = true;
      setTimeout(() => {
        debounceArmed = false;
      }, 1000);
      resetIdleTimer();
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') onActivity();
    }

    resetIdleTimer();

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
    for (const ev of events) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      for (const ev of events) {
        window.removeEventListener(ev, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, organization?.session_timeout_minutes]);

  // Pure fetch: refresh in-context distributors list. Returns the new list
  // so callers can derive state from authoritative data (avoids
  // returned-list-vs-state-async race). Does NOT touch activeDistributor —
  // seeding is first-mount-only per D.14 M1-a SEED-ONCE semantic lock.
  const refreshDistributors = useCallback(async (): Promise<Distributor[]> => {
    try {
      const { data, error } = await supabase
        .from('distributors')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) {
        console.error('[Arbi] Failed to load distributors:', error.message);
        return [];
      }
      const list = (data ?? []) as Distributor[];
      setDistributors(list);
      return list;
    } catch (err) {
      console.warn('[Arbi] Distributors fetch unavailable:', (err as Error).message);
      return [];
    }
  }, []);

  // Load distributors once authenticated. First-mount-only active-distributor
  // seeding via D.14 M1-a SEED-ONCE conditional: subsequent refreshDistributors
  // calls (e.g. post-Add INSERT in DistributorsSection) update the list but
  // never clobber the operator's mid-session selection.
  useEffect(() => {
    if (!user) {
      setDistributors([]);
      setActiveDistributor(null);
      return;
    }

    (async () => {
      const list = await refreshDistributors();
      // SEED-ONCE: only seed when no active selection exists (first-mount
      // cache-miss path). localStorage savedId read inlined here so it
      // fires exactly once per first-mount, never on refresh.
      setActiveDistributor((prev) => {
        if (prev !== null) return prev;
        const savedId = localStorage.getItem('arbi-active-distributor');
        const saved = list.find((d) => d.id === savedId);
        return saved ?? list[0] ?? null;
      });
    })();
  }, [user, refreshDistributors]);

  useEffect(() => {
    if (activeDistributor) {
      localStorage.setItem('arbi-active-distributor', activeDistributor.id);
    }
  }, [activeDistributor]);

  // Fetch credit/cash balances from get_credit_summary RPC
  useEffect(() => {
    // F12 (Day 99a): dist_rep never reads credit (Architecture Rule 4); get_credit_summary
    // is DB-gated to 42501 for dist_rep, so skip the fetch + clear the pill.
    if (isDistRep || !activeDistributor?.id) {
      setCreditBalance(null);
      setCashBalance(null);
      return;
    }

    const fetchCredit = async (): Promise<void> => {
      try {
        const { data, error } = await supabase.rpc('get_credit_summary', {
          p_distributor_id: activeDistributor.id,
        });
        if (error || !data) return;
        const d = data as unknown as { available?: number; hard_draw_mtd?: number };
        setCreditBalance(d.available ?? null);
        setCashBalance(d.hard_draw_mtd ?? null);
      } catch {
        /* swallow */
      }
    };

    fetchCredit();

    const channel = supabase
      .channel('credit-refresh')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'purchase_orders' }, () => {
        fetchCredit();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeDistributor, isDistRep]);

  useEffect(() => {
    localStorage.setItem('arbi-theme', theme);
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('arbi-density', density);
  }, [density]);

  useEffect(() => {
    localStorage.setItem('arbi-confirm-resets', String(confirmResets));
  }, [confirmResets]);

  function toggleTheme(): void {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }
  function toggleDensity(): void {
    setDensity((d) => (d === 'comfortable' ? 'compact' : 'comfortable'));
  }

  async function signOut(): Promise<void> {
    await supabase.auth.signOut();
  }

  return (
    <AppContext.Provider
      value={{
        user,
        session,
        loading,
        theme,
        density,
        sidebarCollapsed,
        setSidebarCollapsed,
        confirmResets,
        setConfirmResets,
        toggleTheme,
        toggleDensity,
        distributors,
        refreshDistributors,
        activeDistributor,
        setActiveDistributor,
        creditBalance,
        cashBalance,
        role,
        isAdmin,
        isDistRep,
        isViewer,
        profileDistributorId,
        fullName,
        setFullName,
        organization,
        setOrganization,
        buyingSessionPrefs,
        setBuyingSessionPrefs,
        signOut,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
